import { Check } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { useMemo, useState, type SubmitEventHandler } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  FieldError,
  Flex,
  Form,
  Separator,
  Text,
  TextArea,
} from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { useCurrentUser } from '@/contexts';
import { useBlocks, useUser } from '@/hooks';
import { analyticsClient } from '@/lib';
import { reportService } from '@/services';
import { fieldLabelStyle } from '@/views/auth/styles';

type Translate = (key: string) => string;

const createReportSchema = (t: Translate) =>
  z.object({
    details: z.string().trim().min(1, t('report.errors.detailsRequired')),
  });

type ReportFormValues = z.infer<ReturnType<typeof createReportSchema>>;

export function ReportProfilePage() {
  const { uid } = useParams<{ uid: string }>();
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const { user: authUser, currentUser } = useCurrentUser();
  const { user: profileUser, isLoading } = useUser(uid);
  const { blockUser } = useBlocks(authUser);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const schema = useMemo(() => createReportSchema(key => t(key)), [t]);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { details: '' },
  });

  const values = useWatch({ control });
  const canSubmit = schema.safeParse(values).success;

  if (uid && currentUser?.uid === uid) {
    return <Navigate to="/account/my-profile" replace />;
  }

  if (isLoading) return null;
  if (!uid || !profileUser) {
    return <Navigate to="/discover" replace />;
  }

  const backToProfile = () => navigate(`/profile/${uid}`);

  const leaveAfterReport = () => {
    setSubmitted(false);
    navigate('/discover');
  };

  const onValid: SubmitEventHandler<HTMLFormElement> = handleSubmit(async data => {
    setSubmitting(true);
    try {
      await reportService.submitReport({
        reportedUid: uid,
        details: data.details,
      });
      analyticsClient.capture('profile_report_submitted');
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit report:', err);
    } finally {
      setSubmitting(false);
    }
  });

  const handleBlockAfterReport = async () => {
    setBlocking(true);
    try {
      await blockUser(profileUser.uid);
      analyticsClient.capture('user_blocked');
      leaveAfterReport();
    } catch (err) {
      console.error('Block relationship failure:', err);
    } finally {
      setBlocking(false);
    }
  };

  return (
    <AccountSubpageShell
      onBack={backToProfile}
      title={t('report.title', { name: profileUser.name })}
      subtitle={t('report.subtitle')}
    >
      <Form.Root onSubmit={onValid} noValidate>
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="1">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('report.detailsLabel')}
            </Text>
            <TextArea
              rows={8}
              placeholder={t('report.placeholder')}
              aria-invalid={errors.details ? true : undefined}
              data-testid="report-details"
              {...register('details')}
            />
            <FieldError message={errors.details?.message} />
          </Flex>

          <Button
            type="submit"
            color="gray"
            highContrast
            fullWidth
            size="lg"
            mt="2"
            disabled={!canSubmit || submitting}
            isLoading={submitting}
            data-testid="send-report"
          >
            {t('report.send')}
          </Button>
          <Button
            type="button"
            variant="outline"
            color="gray"
            fullWidth
            size="lg"
            onClick={backToProfile}
          >
            {t('report.cancel')}
          </Button>
        </Flex>
      </Form.Root>

      <Dialog.Root
        open={submitted}
        onOpenChange={open => {
          if (!open) leaveAfterReport();
        }}
      >
        <Dialog.Content size="1" data-testid="report-success-dialog">
          <Flex direction="column" align="center" gap="3" py="2">
            <Dialog.Title align="center" size="3" weight="medium" mb="0">
              <Flex gap="2" align="center">
                <Check color="var(--accent-9)" size={20} strokeWidth={1.6} aria-hidden />
                {t('report.successTitle')}
              </Flex>
            </Dialog.Title>
            <Dialog.Description align="center" size="1" color="gray" mb="0">
              {t('report.successMessage')}
            </Dialog.Description>
            <Separator size="4" />
            <Flex direction="column" gap="2" width="100%" mt="2">
              <Text size="1" weight="medium" align="center">
                {t('report.blockPromptTitle')}
              </Text>
              <Text size="0.5" color="gray" align="center">
                {t('report.blockPromptBody')}
              </Text>
              <Flex direction="column" gap="2" mt="1" width="100%">
                <Button
                  type="button"
                  variant="outline"
                  color="gray"
                  fullWidth
                  onClick={leaveAfterReport}
                  data-testid="cancel-block"
                >
                  {t('report.blockNo')}
                </Button>
                <Button
                  type="button"
                  color="gray"
                  highContrast
                  fullWidth
                  isLoading={blocking}
                  onClick={() => void handleBlockAfterReport()}
                  data-testid="confirm-block"
                >
                  {t('report.blockYes')}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </AccountSubpageShell>
  );
}
